import React from 'react'
import styles from './CaseFirstViewItem.module.scss'
import Tags from '../../../commons/Tags/Tags'
import { useEffect, useRef, ComponentPropsWithoutRef } from 'react'
import { gsap, Power4 } from 'gsap'
import Link from 'next/link'

type CaseFirstViewItemProps = {
  title1: string
  title2?: string
  subtitle?: string
  introduction_text: string
  company: string
  tag1text: string
  tag2text?: string
  tag3text?: string
  tag1src: string
  tag2src?: string
  tag3src?: string
  color: string
  mss?: boolean
  nagoyatv?: boolean
  staff1?: string
  staff2?: string
  staff3?: string
  linkText?: string
  linkUrl?: string
  industryTagText?: string
  industryTagSrc?: string
  award?: string
  awardAlt?: string
} & ComponentPropsWithoutRef<'div'>

const tweenVars: gsap.TimelineVars = [
  {
    y: '50',
    duration: 1,
    opacity: 0,
    ease: Power4.easeOut,
  },
  {
    x: '-50',
    duration: 0.5,
    opacity: 0,
    ease: Power4.easeOut,
  },
  {
    duration: 1,
    width: '40px',
    border: '1px solid #3C3C3C',
    transform: 'scaleY(0.5)',
    transformOrigin: '0 100%',
    ease: Power4.easeOut,
  },
  {
    duration: 1,
    width: '40px',
    border: '1px solid #fff',
    transform: 'scaleY(0.5)',
    transformOrigin: '0 100%',
    ease: Power4.easeOut,
  },
]

const CaseFirstViewItem: React.FC<CaseFirstViewItemProps> = ({
  title1,
  // title2,
  subtitle,
  introduction_text,
  company,
  tag1text,
  tag2text,
  tag3text,
  tag1src,
  tag2src,
  tag3src,
  industryTagText,
  industryTagSrc,
  color,
  // mss,
  // nagoyatv,
  staff1,
  staff2,
  staff3,
  linkText,
  linkUrl,
  award,
  awardAlt,
  // ...props
}) => {
  const timeline = gsap.timeline()

  const wrapper = useRef<HTMLDivElement>()
  const service_name = useRef<HTMLHeadingElement>()
  const border = useRef<HTMLDivElement>()
  const title = useRef<HTMLDivElement>()
  const sub_title = useRef<HTMLDivElement>()
  const introduction = useRef<HTMLParagraphElement>()
  const sub_text = useRef<HTMLDivElement>()
  const sub_text1 = useRef<HTMLDivElement>()
  const sub_text2 = useRef<HTMLDivElement>()
  const sub_text3 = useRef<HTMLDivElement>()
  const link = useRef<HTMLDivElement>()
  const tag = useRef<HTMLDivElement>()

  const firstCheckRef = useRef<boolean>(false)

  useEffect(() => {
    if (!firstCheckRef.current) {
      color === '#fff'
        ? timeline
            .to(wrapper.current, { css: { opacity: 1 } }, 0)
            .from(service_name.current, tweenVars[0], 0.4)
            .to(border.current, tweenVars[3], 0.6)
            .from(title.current, tweenVars[0], 0.8)
            .from(sub_title.current, tweenVars[0], 0.8)
            .from(introduction.current, tweenVars[0], 1)
            .from(sub_text.current, tweenVars[0], 1.2)
            .from(sub_text1.current, tweenVars[0], 1.3)
            .from(sub_text2.current, tweenVars[0], 1.3)
            .from(sub_text3.current, tweenVars[0], 1.3)
            .from(link.current, tweenVars[0], 1.3)
            .from(tag.current, tweenVars[0], 1.4)
        : timeline
            .to(wrapper.current, { css: { opacity: 1 } }, 0)
            .from(service_name.current, tweenVars[0], 0.4)
            .to(border.current, tweenVars[2], 0.6)
            .from(title.current, tweenVars[0], 0.8)
            .from(sub_title.current, tweenVars[0], 0.8)
            .from(introduction.current, tweenVars[0], 1)
            .from(sub_text.current, tweenVars[0], 1.2)
            .from(sub_text1.current, tweenVars[0], 1.3)
            .from(sub_text2.current, tweenVars[0], 1.3)
            .from(sub_text3.current, tweenVars[0], 1.3)
            .from(link.current, tweenVars[0], 1.3)
            .from(tag.current, tweenVars[0], 1.4)
      firstCheckRef.current = true
    }
  }, [])

  const color_style = {
    color: color,
  }

  return (
    <>
      <div className={styles.fv} style={color_style}>
        <div className={styles.fv_inner}>
          <div className={styles.fv_inner_subttl} ref={sub_title}>
            {subtitle ?? <h2>{subtitle}</h2>}
          </div>
          <h1 className={styles.fv_inner_ttl} ref={title}>
            {title1}
          </h1>
          <p className={styles.fv_inner_txt} ref={introduction}>
            {introduction_text}
          </p>
          <div
            className={`${styles.fv_inner_tags} ${award ? styles.fv_inner_tags_hasAward : ''}`}
            ref={tag}
          >
            <Tags
              tag1src={tag1src}
              tag1text={tag1text}
              tag2src={tag2src}
              tag2text={tag2text}
              tag3src={tag3src}
              tag3text={tag3text}
              industryTagText={industryTagText}
              industryTagSrc={industryTagSrc}
            />
            {award && <img src={award} alt={awardAlt} className={styles.fv_inner_award} />}
          </div>
          <div className={styles.fv_inner_sub}>
            <p className={styles.fv_inner_sub_company} ref={sub_text}>
              {company}
            </p>
            {staff1 && (
              <div className={styles.fv_inner_sub_staff}>
                {staff1 && <p ref={sub_text1}>{staff1}</p>}
                {staff2 && <p ref={sub_text2}>{staff2}</p>}
                {staff3 && <p ref={sub_text3}>{staff3}</p>}
              </div>
            )}
            {linkText && (
              <p className={styles.fv_inner_link } ref={link}>
                <a href={linkUrl} target='_blank'>{linkText}</a>
              </p>
            )}
          </div>
        </div>
      </div>
    </>
  )
}

export default CaseFirstViewItem
